/* eslint-disable max-lines */
import { CameraDetail, Point as CameraPoint } from '@hakimo-ui/hakimo/types';
import {
  CanvasDimensions,
  Point,
  SpecialZoneType,
  ZonePolygonData,
} from './types';

/**
 * Resolution of the original image (natural dimensions)
 */
export interface ImageResolution {
  width: number;
  height: number;
}

export interface SpecialZones {
  polygons: ZonePolygonData[];
  resolution?: ImageResolution; // space the polygons are defined in
}

export const ZONE_COLORS = {
  [SpecialZoneType.DeadZone]: 'rgba(11, 13, 16, 0.5)',
  [SpecialZoneType.VehicleParkingZone]: 'rgba(67, 110, 226, 0.3)',
  [SpecialZoneType.ActiveZone]: 'rgba(246, 120, 31, 0.3)',
  [SpecialZoneType.PresenceZone]: 'rgba(27, 127, 121, 0.3)',
};

export const POLYGON_LINE_STROKE_COLOR = '#d44536';

/**
 * Zone overlay pattern configuration.
 * Each zone type gets a distinct stripe/crosshatch pattern with dual-color
 * stripes so at least one color always contrasts with the underlying image.
 */
export interface ZonePatternConfig {
  /** Primary stripe color */
  stripeColor: string;
  /** Background color between stripes */
  bgColor: string;
  /** Stroke color for the inner border */
  strokeColor: string;
  /** Whether to use crosshatch (two directions) instead of single diagonal */
  crosshatch: boolean;
}

export const ZONE_PATTERN_CONFIGS: Record<SpecialZoneType, ZonePatternConfig> =
  {
    [SpecialZoneType.DeadZone]: {
      stripeColor: 'rgba(200, 200, 200, 0.22)',
      bgColor: 'rgba(11, 13, 16, 0.35)',
      // strokeColor: 'rgba(140, 140, 140, 0.65)',
      strokeColor: POLYGON_LINE_STROKE_COLOR,
      crosshatch: true,
    },
    [SpecialZoneType.VehicleParkingZone]: {
      stripeColor: 'rgba(67, 110, 226, 0.22)',
      bgColor: 'rgba(67, 110, 226, 0.1)',
      strokeColor: POLYGON_LINE_STROKE_COLOR,
      crosshatch: false,
    },
    [SpecialZoneType.ActiveZone]: {
      stripeColor: 'rgba(246, 120, 31, 0.25)',
      bgColor: 'rgba(246, 120, 31, 0.1)',
      strokeColor: POLYGON_LINE_STROKE_COLOR,
      crosshatch: false,
    },
    [SpecialZoneType.PresenceZone]: {
      stripeColor: 'rgba(27, 127, 121, 0.25)',
      bgColor: 'rgba(27, 127, 121, 0.1)',
      strokeColor: 'rgba(27, 127, 121, 0.9)',
      crosshatch: false,
    },
  };

/** Outer halo stroke color — subtle white for contrast on dark images */
export const ZONE_HALO_STROKE_COLOR = 'rgba(255, 255, 255, 0.35)';

// Konva's fillPatternImage accepts HTMLCanvasElement directly, so we skip the
// Image()+data-URL round-trip — that round-trip is async and would leave the
// polygon unfilled on first paint until the image decodes.
export function createZonePatternImage(
  config: ZonePatternConfig,
  tileSize = 14
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = tileSize;
  canvas.height = tileSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = config.bgColor;
  ctx.fillRect(0, 0, tileSize, tileSize);

  ctx.strokeStyle = config.stripeColor;
  ctx.lineWidth = tileSize / 5;

  ctx.beginPath();
  ctx.moveTo(0, tileSize);
  ctx.lineTo(tileSize, 0);
  ctx.moveTo(-tileSize, tileSize);
  ctx.lineTo(tileSize, -tileSize);
  ctx.moveTo(0, tileSize * 2);
  ctx.lineTo(tileSize * 2, 0);
  ctx.stroke();

  if (config.crosshatch) {
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(tileSize, tileSize);
    ctx.moveTo(-tileSize, 0);
    ctx.lineTo(tileSize, tileSize * 2);
    ctx.moveTo(0, -tileSize);
    ctx.lineTo(tileSize * 2, tileSize);
    ctx.stroke();
  }

  return canvas;
}

const patternImageCache = new Map<SpecialZoneType, HTMLCanvasElement>();

// Lazy so this module stays import-safe in non-DOM envs (jest without
// jsdom-canvas). The canvas is only built when a zone first renders.
export function getZonePatternImage(type: SpecialZoneType): HTMLCanvasElement {
  let canvas = patternImageCache.get(type);
  if (!canvas) {
    canvas = createZonePatternImage(ZONE_PATTERN_CONFIGS[type]);
    patternImageCache.set(type, canvas);
  }
  return canvas;
}
/**
 * SVG path data for zone type icons (24x24 viewBox).
 * Rendered as a single centered icon inside each zone polygon.
 */
export interface ZoneIconConfig {
  /** SVG path data string (24x24 viewBox) */
  path: string;
  /** Fill color for the icon */
  fill: string;
  /** Icon opacity */
  opacity: number;
}

// Bold X icon — dead/no-go zone
const X_PATH =
  'M20 6.91L17.09 4L12 9.09L6.91 4L4 6.91L9.09 12L4 17.09L6.91 20L12 14.91L17.09 20L20 17.09L14.91 12L20 6.91Z';

// Car icon — vehicle parking zone
const CAR_PATH =
  'M18.92,6.01C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6.01L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6.01M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M5,11L6.5,6.5H17.5L19,11H5Z';

// Lightning bolt icon — active monitoring zone
const BOLT_PATH = 'M7,2V13H10V22L17,10H13L17,2H7Z';

// Two people — presence / counting zone
const PEOPLE_PATH =
  'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z';

export const ZONE_ICON_CONFIGS: Record<SpecialZoneType, ZoneIconConfig> = {
  [SpecialZoneType.DeadZone]: {
    path: X_PATH,
    fill: 'rgba(255, 255, 255, 0.4)',
    opacity: 1,
  },
  [SpecialZoneType.VehicleParkingZone]: {
    path: CAR_PATH,
    fill: 'rgba(67, 110, 226, 0.5)',
    opacity: 1,
  },
  [SpecialZoneType.ActiveZone]: {
    path: BOLT_PATH,
    fill: 'rgba(246, 120, 31, 0.5)',
    opacity: 1,
  },
  [SpecialZoneType.PresenceZone]: {
    path: PEOPLE_PATH,
    fill: 'rgba(27, 127, 121, 0.55)',
    opacity: 1,
  },
};

function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function distanceToPolygonEdges(p: Point, polygon: Point[]): number {
  let minDist = Infinity;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j];
    const b = polygon[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    let t = 0;
    if (lenSq > 0) {
      t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    const ddx = p.x - cx;
    const ddy = p.y - cy;
    const d = Math.sqrt(ddx * ddx + ddy * ddy);
    if (d < minDist) minDist = d;
  }
  return minDist;
}

// Pole of inaccessibility: the inside point farthest from any edge. Used so
// the centered zone icon stays inside even for irregular/concave polygons,
// where a vertex-mean centroid can fall outside the shape. `radius` is the
// distance from `center` to the nearest edge — i.e. the inscribed-circle
// radius at that point — also used to size the icon so it can't overflow.
// Simplified polylabel via uniform grid search; cheap enough that callers
// can recompute on point changes (typically memoized by useMemo).
export function getPolygonIconPlacement(polygon: Point[]): {
  center: Point;
  radius: number;
} {
  if (polygon.length === 0) return { center: { x: 0, y: 0 }, radius: 0 };

  let minX = polygon[0].x;
  let maxX = polygon[0].x;
  let minY = polygon[0].y;
  let maxY = polygon[0].y;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const width = maxX - minX;
  const height = maxY - minY;
  const fallback: Point = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };

  if (width === 0 || height === 0) return { center: fallback, radius: 0 };

  const step = Math.min(width, height) / 24;
  let bestPoint = fallback;
  let bestRadius = -Infinity;

  for (let x = minX + step / 2; x < maxX; x += step) {
    for (let y = minY + step / 2; y < maxY; y += step) {
      const p = { x, y };
      if (!isPointInPolygon(p, polygon)) continue;
      const r = distanceToPolygonEdges(p, polygon);
      if (r > bestRadius) {
        bestRadius = r;
        bestPoint = p;
      }
    }
  }

  if (bestRadius < 0) return { center: fallback, radius: 0 };

  return { center: bestPoint, radius: bestRadius };
}

// Icon viewBox is 24×24; size it so its bounding square fits within the
// inscribed circle (factor √2/2 ≈ 0.7 — slight margin keeps it from kissing
// the polygon edges). Clamped so very small zones still show a visible icon
// without ever overflowing.
export function getZoneIconScale(inscribedRadius: number): number {
  if (inscribedRadius <= 0) return 0;
  const scale = (inscribedRadius * 2 * 0.7) / 24;
  return Math.max(0.4, Math.min(scale, 2.5));
}

/**
 * Converts a point from camera format [x, y] to our format { x, y }
 * and scales from image resolution to canvas dimensions
 */
export function cameraPointToCanvasPoint(
  point: CameraPoint,
  imageResolution: ImageResolution,
  canvasDimensions: CanvasDimensions
): Point {
  const scaleX = canvasDimensions.width / imageResolution.width;
  const scaleY = canvasDimensions.height / imageResolution.height;

  return {
    x: point[0] * scaleX,
    y: point[1] * scaleY,
  };
}

/**
 * Converts a polygon from camera format Point[][] to our ZonePolygonData format
 * scaling from image resolution to canvas dimensions
 */
export function cameraZoneToCanvasZone(
  points: CameraPoint[],
  id: string,
  type: SpecialZoneType,
  imageResolution: ImageResolution,
  canvasDimensions: CanvasDimensions
): ZonePolygonData {
  return {
    id,
    type,
    points: points.map((p) =>
      cameraPointToCanvasPoint(p, imageResolution, canvasDimensions)
    ),
  };
}

export function getInitialZones(
  camera: CameraDetail,
  imageResolution: ImageResolution,
  canvasDimensions: CanvasDimensions
): ZonePolygonData[] {
  const result: ZonePolygonData[] = [];

  camera.deadZones?.forEach((points) => {
    result.push(
      cameraZoneToCanvasZone(
        points,
        generateZoneId(),
        SpecialZoneType.DeadZone,
        imageResolution,
        canvasDimensions
      )
    );
  });

  camera.vehicleParkingZones?.forEach((points) => {
    result.push(
      cameraZoneToCanvasZone(
        points,
        generateZoneId(),
        SpecialZoneType.VehicleParkingZone,
        imageResolution,
        canvasDimensions
      )
    );
  });

  camera.activeZones?.forEach((points) => {
    result.push(
      cameraZoneToCanvasZone(
        points,
        generateZoneId(),
        SpecialZoneType.ActiveZone,
        imageResolution,
        canvasDimensions
      )
    );
  });

  return result;
}

/**
 * Constrains a point to stay within canvas boundaries
 */
export function constrainPointToBounds(
  point: Point,
  bounds: CanvasDimensions
): Point {
  return {
    x: Math.max(0, Math.min(point.x, bounds.width)),
    y: Math.max(0, Math.min(point.y, bounds.height)),
  };
}

/**
 * Calculates the midpoint between two points
 */
export function getMidpoint(p1: Point, p2: Point): Point {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

/**
 * Converts polygon points array to flat array for Konva Line
 * [{ x: 10, y: 20 }, { x: 30, y: 40 }] -> [10, 20, 30, 40]
 */
export function pointsToFlatArray(points: Point[]): number[] {
  return points.flatMap((p) => [p.x, p.y]);
}

/**
 * Creates a default rectangle zone centered in the canvas
 */
export function createDefaultZone(
  canvasDimensions: CanvasDimensions,
  type: SpecialZoneType
): ZonePolygonData {
  const { width, height } = canvasDimensions;
  const rectWidth = width * 0.3;
  const rectHeight = height * 0.3;
  const centerX = width / 2;
  const centerY = height / 2;

  return {
    id: generateZoneId(),
    type,
    points: [
      { x: centerX - rectWidth / 2, y: centerY - rectHeight / 2 },
      { x: centerX + rectWidth / 2, y: centerY - rectHeight / 2 },
      { x: centerX + rectWidth / 2, y: centerY + rectHeight / 2 },
      { x: centerX - rectWidth / 2, y: centerY + rectHeight / 2 },
    ],
  };
}

/**
 * Calculates all midpoints for a polygon's edges
 */
export function calculateMidpoints(points: Point[]): Point[] {
  return points.map((point, index) => {
    const nextPoint = points[(index + 1) % points.length];
    return getMidpoint(point, nextPoint);
  });
}

/**
 * Inserts a new vertex at a midpoint position
 * Returns new points array with the vertex inserted
 */
export function insertVertexAtMidpoint(
  points: Point[],
  midpointIndex: number,
  newPosition: Point
): Point[] {
  const insertIndex = midpointIndex + 1;
  const newPoints = [...points];
  newPoints.splice(insertIndex, 0, newPosition);
  return newPoints;
}

/**
 * Moves all polygon points by a delta while respecting boundaries
 */
export function movePolygon(
  points: Point[],
  deltaX: number,
  deltaY: number,
  bounds: CanvasDimensions
): Point[] {
  const movedPoints = points.map((p) => ({
    x: p.x + deltaX,
    y: p.y + deltaY,
  }));

  const minX = Math.min(...movedPoints.map((p) => p.x));
  const maxX = Math.max(...movedPoints.map((p) => p.x));
  const minY = Math.min(...movedPoints.map((p) => p.y));
  const maxY = Math.max(...movedPoints.map((p) => p.y));

  let adjustX = 0;
  let adjustY = 0;

  if (minX < 0) adjustX = -minX;
  if (maxX > bounds.width) adjustX = bounds.width - maxX;
  if (minY < 0) adjustY = -minY;
  if (maxY > bounds.height) adjustY = bounds.height - maxY;

  return movedPoints.map((p) => ({
    x: p.x + adjustX,
    y: p.y + adjustY,
  }));
}

/**
 * Rescales a point from one canvas dimension to another
 */
export function rescalePoint(
  point: Point,
  oldDimensions: CanvasDimensions,
  newDimensions: CanvasDimensions
): Point {
  const scaleX = newDimensions.width / oldDimensions.width;
  const scaleY = newDimensions.height / oldDimensions.height;

  return {
    x: point.x * scaleX,
    y: point.y * scaleY,
  };
}

/**
 * Rescales all zones from one canvas dimension to another
 */
export function rescaleZones(
  zones: ZonePolygonData[],
  oldDimensions: CanvasDimensions,
  newDimensions: CanvasDimensions
): ZonePolygonData[] {
  return zones.map((zone) => ({
    ...zone,
    points: zone.points.map((p) =>
      rescalePoint(p, oldDimensions, newDimensions)
    ),
  }));
}

/**
 * Converts a point from canvas format { x, y } to camera format [x, y]
 * and scales from canvas dimensions to image resolution
 */
export function canvasPointToCameraPoint(
  point: Point,
  canvasDimensions: CanvasDimensions,
  imageResolution: ImageResolution
): CameraPoint {
  const scaleX = imageResolution.width / canvasDimensions.width;
  const scaleY = imageResolution.height / canvasDimensions.height;

  return [point.x * scaleX, point.y * scaleY];
}

/**
 * Converts a canvas zone's points to camera format Point[]
 */
export function canvasZoneToCameraPoints(
  zone: ZonePolygonData,
  canvasDimensions: CanvasDimensions,
  imageResolution: ImageResolution
): CameraPoint[] {
  return zone.points.map((p) =>
    canvasPointToCameraPoint(p, canvasDimensions, imageResolution)
  );
}

/**
 * Converts all canvas zones of a specific type to camera format Point[][]
 */
export function getZonesByTypeAsCameraFormat(
  zones: ZonePolygonData[],
  type: SpecialZoneType,
  canvasDimensions: CanvasDimensions,
  imageResolution: ImageResolution
): CameraPoint[][] {
  return zones
    .filter((zone) => zone.type === type)
    .map((zone) =>
      canvasZoneToCameraPoints(zone, canvasDimensions, imageResolution)
    );
}

/**
 * Generates a unique zone ID
 */
export function generateZoneId(): string {
  return crypto.randomUUID();
}
